import React, { useState } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { StatusBadge } from '../components/common/StatusBadge';
import { PlatformIcon, PLATFORMS } from '../components/common/PlatformIcon';
import { EmptyState } from '../components/common/EmptyState';
import { postsApi } from '../lib/api';
import { Plus, Search, FileText, Send, Copy, Trash2, MoreHorizontal } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

export default function CompanyPosts() {
  const { companyId } = useOutletContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [platformFilter, setPlatformFilter] = useState('all');

  const { data, isLoading } = useQuery({
    queryKey: ['posts', companyId, search, statusFilter, platformFilter],
    queryFn: () => {
      const params = { search };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (platformFilter !== 'all') params.platform = platformFilter;
      return postsApi.list(companyId, params).then((r) => r.data);
    },
  });

  const publishMutation = useMutation({
    mutationFn: (postId) => postsApi.publish(companyId, postId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['posts', companyId] });
      toast.success('Post published successfully');
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Publish failed'),
  });

  const duplicateMutation = useMutation({
    mutationFn: (postId) => postsApi.duplicate(companyId, postId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['posts', companyId] });
      toast.success('Post duplicated');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (postId) => postsApi.delete(companyId, postId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['posts', companyId] });
      toast.success('Post deleted');
    },
  });

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold font-display">Posts</h2>
          <p className="text-sm text-muted-foreground mt-1">Manage content for this company</p>
        </div>
        <Button onClick={() => navigate(`/companies/${companyId}/posts/new`)} data-testid="create-post-button">
          <Plus className="w-4 h-4 mr-2" /> New Post
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search posts..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 w-64"
            data-testid="posts-search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36" data-testid="posts-status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="scheduled">Scheduled</SelectItem>
            <SelectItem value="published">Published</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={platformFilter} onValueChange={setPlatformFilter}>
          <SelectTrigger className="w-40" data-testid="posts-platform-filter">
            <SelectValue placeholder="Platform" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Platforms</SelectItem>
            {PLATFORMS.map((p) => (
              <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Posts Table */}
      {isLoading ? (
        <div className="space-y-3">{[1, 2, 3, 4].map((i) => <div key={i} className="h-14 bg-muted animate-pulse rounded-lg" />)}</div>
      ) : data?.posts?.length > 0 ? (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Post</TableHead>
                <TableHead>Platforms</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Scheduled</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.posts.map((post) => (
                <TableRow
                  key={post._id}
                  className="cursor-pointer hover:bg-muted/60"
                  onClick={() => navigate(`/companies/${companyId}/posts/${post._id}/edit`)}
                >
                  <TableCell>
                    <p className="font-medium text-sm truncate max-w-xs">
                      {post.title || post.caption?.substring(0, 50) || 'Untitled'}
                    </p>
                    {post.hashtags?.length > 0 && (
                      <p className="text-xs text-muted-foreground truncate max-w-xs">
                        {post.hashtags.join(' ')}
                      </p>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {post.platform_targets?.map((p) => (
                        <PlatformIcon key={p} platform={p} size="xs" />
                      ))}
                    </div>
                  </TableCell>
                  <TableCell><StatusBadge status={post.status} /></TableCell>
                  <TableCell>
                    <span className="text-xs text-muted-foreground capitalize">{post.post_type}</span>
                  </TableCell>
                  <TableCell>
                    {post.scheduled_at ? (
                      <span className="text-xs text-muted-foreground">
                        {new Date(post.scheduled_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                        {post.status !== 'published' && (
                          <DropdownMenuItem onClick={() => publishMutation.mutate(post._id)}>
                            <Send className="w-4 h-4 mr-2" /> Publish Now
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => duplicateMutation.mutate(post._id)}>
                          <Copy className="w-4 h-4 mr-2" /> Duplicate
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => deleteMutation.mutate(post._id)} className="text-destructive">
                          <Trash2 className="w-4 h-4 mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <EmptyState
          icon={FileText}
          title="No posts yet"
          description="Create your first post to start managing content."
          actionLabel="New Post"
          onAction={() => navigate(`/companies/${companyId}/posts/new`)}
        />
      )}
    </div>
  );
}
